import { DateRange } from '../date-range';
import {
  DataClassificationMultiSelect,
  MultiSelect,
  ProviderMultiSelect,
  SeverityMultiSelect,
  StatusMultiSelect,
} from '../multi-select';
import { SkeletonLoader } from '../skeleton-loader';
import { AssetGroupFC } from './AssetGroupFilter';
import { EnvironmentFC } from './EnvironmentFilter';
import { IntegrationGroupFC } from './IntegrationGroupFilter';
import { CriticalStatusFilter } from './CriticalStatusFilter';
import { DetectionFilterFC } from './DetectionFilter';
import { IntegrationFilterFC } from './IntegrationFilter';
import { RegionFilterFC } from './RegionFilter';
import { ResourceTypeFilterFC } from './ResourceTypeFilter';
import { RiskScoreFilter } from './RiskScoreFilter';
import { RangeFilter } from './range-filter';
import {
  readRangeFilterValue,
  toRangeFilterBounds,
} from './range-filter-value';
import { readSingleFilterValue } from './filter-value';
import { SearchFilter } from './SearchFilter';
import { TenantFC } from './TenantFilter';
import { Filter, SetFilterValue } from './filter-types';
import { ElasticIpFilter } from './ElasticIpFilter';
import { TagFilter, ComparisonOperator } from './TagFilter';
import { ComboFilterElement } from './ComboFilter';
import { MultiSelectWithSuggestions } from '../../experimental/atoms/MultiSelectWithSuggestions/MultiSelectWithSuggestions';

import { useTranslation } from 'react-i18next';

export const FilterElement = ({
  filter,
  filters,
  mode,
  filterValues,
  clearCounts,
  setFilterValue,
  removeFilter,
}: {
  filter: Filter;
  filters: Filter[];
  mode: string;
  filterValues: Record<string, unknown>;
  /** How many times each filter has been emptied, e.g. by Clear all. */
  clearCounts?: Record<string, number>;
  setFilterValue: SetFilterValue;
  removeFilter: (filter?: Filter | undefined) => void;
}) => {
  const { t } = useTranslation('designSystem');
  switch (filter?.type) {
    case 'combo':
      return (
        <ComboFilterElement
          {...filter}
          key={filter.id}
          filters={filter.filters.map((f) => ({
            ...f,
            permanent: true,
            wrapperClassName: `rounded-tl-none rounded-bl-none border-l-0 ${f.wrapperClassName}`,
          }))}
          mode={mode}
          filterValues={filterValues}
          setFilterValue={setFilterValue}
          removeFilter={removeFilter}
        />
      );
    case 'integrationId':
      return (
        <IntegrationFilterFC
          {...filter}
          key={filter.id}
          value={(filterValues[filter.id] as string[]) || []}
          setValue={(x) => setFilterValue(filter.id, x, filter)}
          filters={filters}
          filteredValues={filterValues}
          onClose={() => {
            removeFilter(filter);
          }}
        />
      );
    case 'resourceType':
      return (
        <ResourceTypeFilterFC
          {...filter}
          key={filter.id}
          value={(filterValues[filter.id] as string[]) || []}
          setValue={(x) => setFilterValue(filter.id, x, filter)}
          filters={filters}
          filteredValues={filterValues}
          onClose={() => {
            removeFilter(filter);
          }}
        />
      );
    case 'region':
      return (
        <RegionFilterFC
          {...filter}
          key={filter.id}
          value={(filterValues[filter.id] as string[]) || []}
          setValue={(x) => setFilterValue(filter.id, x, filter)}
          filters={filters}
          filteredValues={filterValues}
          onClose={() => {
            removeFilter(filter);
          }}
        />
      );
    case 'detectionId':
      return (
        <DetectionFilterFC
          {...filter}
          key={filter.id}
          value={(filterValues[filter.id] as string[]) || []}
          setValue={(x) => setFilterValue(filter.id, x, filter)}
          filters={filters}
          filteredValues={filterValues}
          onClose={() => {
            removeFilter(filter);
          }}
        />
      );
    case 'assetGroup':
      return (
        <AssetGroupFC
          {...filter}
          key={filter.id}
          value={(filterValues[filter.id] as string[]) || []}
          setValue={(x) => setFilterValue(filter.id, x, filter)}
          filters={filters}
          filteredValues={filterValues}
          onClose={() => {
            removeFilter(filter);
          }}
        />
      );
    case 'integrationGroup':
      return (
        <IntegrationGroupFC
          {...filter}
          key={filter.id}
          value={(filterValues[filter.id] as string[]) || []}
          setValue={(x) => setFilterValue(filter.id, x, filter)}
          filters={filters}
          filteredValues={filterValues}
          onClose={() => {
            removeFilter(filter);
          }}
        />
      );
    case 'environment':
      return (
        <EnvironmentFC
          {...filter}
          key={filter.id}
          value={(filterValues[filter.id] as string[]) || []}
          setValue={(x) => setFilterValue(filter.id, x, filter)}
          onClose={() => {
            removeFilter(filter);
          }}
        />
      );
    case 'multiSelect':
      return (
        <MultiSelect
          {...filter}
          value={(filterValues[filter.id] as string[]) || []}
          setValue={(x) => setFilterValue(filter.id, x, filter)}
          options={filter.options}
          label={filter.label}
          key={filter.id}
          onClose={() => {
            removeFilter(filter);
          }}
          icon={filter.icon}
          displayButtonClassName={filter.wrapperClassName}
          dataTestId={filter.dataTestId || `filter-${filter.id}`}
        />
      );
    case 'select':
      return (
        <SkeletonLoader
          status={
            filter.loading ? 'loading' : filter.error ? 'error' : 'success'
          }
          error={filter.error}
          type="line"
          key={filter.id}
        >
          <>
            {filter.multiple ? (
              <MultiSelect
                {...filter}
                value={(filterValues[filter.id] as string[]) || []}
                setValue={(x) => setFilterValue(filter.id, x, filter)}
                options={filter.options}
                mode="multiple"
                label={filter.label}
                key={filter.id}
                onClose={() => {
                  removeFilter(filter);
                }}
                icon={filter.icon}
                displayButtonClassName={filter.wrapperClassName}
                dataTestId={filter.dataTestId || `filter-${filter.id}`}
              />
            ) : (
              <MultiSelect
                {...filter}
                value={(filterValues[filter.id] as string) || ''}
                setValue={(x) => setFilterValue(filter.id, x, filter)}
                options={filter.options}
                mode="single"
                label={filter.label}
                key={filter.id}
                onClose={() => {
                  removeFilter(filter);
                }}
                icon={filter.icon}
                displayButtonClassName={filter.wrapperClassName}
                dataTestId={filter.dataTestId || `filter-${filter.id}`}
              />
            )}
            {filter.helperText && (
              <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {filter.helperText}
              </span>
            )}
          </>
        </SkeletonLoader>
      );
    case 'provider':
      return (
        <ProviderMultiSelect
          {...filter}
          value={(filterValues[filter.id] as string[]) || []}
          mode="multiple"
          setValue={(x: string[]) => setFilterValue(filter.id, x, filter)}
          label={filter.label}
          key={filter.id}
          onClose={() => {
            removeFilter(filter);
          }}
          displayButtonClassName={filter.wrapperClassName}
        />
      );
    case 'status':
      return (
        <StatusMultiSelect
          {...filter}
          value={(filterValues[filter.id] || '') as string}
          mode="single"
          setValue={(x: string | undefined) =>
            setFilterValue(filter.id, x, filter)
          }
          label={filter.label}
          key={filter.id}
          onClose={() => {
            removeFilter(filter);
          }}
          displayButtonClassName={filter.wrapperClassName}
        />
      );

    case 'criticalStatus':
      return (
        <CriticalStatusFilter
          {...filter}
          mode="single"
          value={(filterValues[filter.id] || '') as string}
          setValue={(x: string | undefined) => {
            setFilterValue(filter.id, x, filter);
          }}
          label={filter.label}
          key={filter.id}
          onClose={() => {
            removeFilter(filter);
          }}
          displayButtonClassName={filter.wrapperClassName}
        />
      );

    case 'severityLevel':
      return (
        <SeverityMultiSelect
          {...filter}
          value={((filterValues[filter.id] || []) as string[]) || []}
          mode="multiple"
          setValue={(x: string[]) => setFilterValue(filter.id, x, filter)}
          label={filter.label}
          key={filter.id}
          onClose={() => {
            removeFilter(filter);
          }}
          displayButtonClassName={filter.wrapperClassName}
        />
      );

    case 'exposedSecretLevel':
      return (
        <SeverityMultiSelect
          {...filter}
          value={((filterValues[filter.id] || []) as string[]) || []}
          mode="multiple"
          setValue={(x: string[]) => setFilterValue(filter.id, x, filter)}
          label={filter.label}
          key={filter.id}
          onClose={() => {
            removeFilter(filter);
          }}
          displayButtonClassName={filter.wrapperClassName}
        />
      );

    case 'dateRange': {
      const startDate = filterValues[filter.startKey] as string;
      const endDate = filterValues[filter.endKey] as string;
      return (
        <DateRange
          {...filter}
          filterView
          key={filter.id}
          value={
            startDate && endDate
              ? {
                  startDate: startDate,
                  endDate: endDate,
                  type: 'absolute',
                }
              : undefined
          }
          placeholder={
            filter.placeholder ||
            t('selectLabelDateRange', { label: filter.label })
          }
          label={filter.label}
          setDateRange={(date) => {
            setFilterValue(filter.startKey, date?.startDate, filter);
            setFilterValue(filter.endKey, date?.endDate, filter);
          }}
          onClose={() => {
            removeFilter(filter);
          }}
        />
      );
    }
    case 'search':
      return (
        <SearchFilter
          {...filter}
          key={filter.id}
          value={readSingleFilterValue(filterValues[filter.id], mode)}
          clearCount={clearCounts?.[filter.id]}
          placeholder={filter.label || ''}
          setValue={setFilterValue}
        />
      );
    case 'elasticIp': {
      let ipValue = filterValues[filter.id] || [];
      if (filterValues[filter.id]) {
        ipValue = [...(ipValue as string[])];
      }
      return (
        <ElasticIpFilter
          {...filter}
          key={filter.id}
          value={ipValue as string[]}
          clearCount={clearCounts?.[filter.id]}
          placeholder={filter.label || ''}
          setValue={(x: string[]) => setFilterValue(filter.id, x, filter)}
        />
      );
    }
    case 'riskScore':
      return (
        <RiskScoreFilter
          {...filter}
          key={filter.id}
          value={readSingleFilterValue(filterValues[filter.id], mode)}
          setValue={setFilterValue}
          label={filter.label}
          onClose={() => {
            removeFilter(filter);
          }}
        />
      );
    case 'range':
      return (
        <RangeFilter
          key={filter.id}
          label={filter.label}
          value={readRangeFilterValue(filterValues, filter)}
          min={filter.min}
          max={filter.max}
          step={filter.step}
          formatValue={filter.formatValue}
          caption={filter.caption}
          describeValue={filter.describeValue}
          disabled={filter.disabled}
          permanent={filter.permanent}
          showClear={filter.showClear}
          dataTestId={filter.dataTestId}
          onCommit={(range) => {
            setFilterValue(filter.id, toRangeFilterBounds(range), filter);
          }}
          onClear={() => {
            setFilterValue(filter.id, undefined, filter);
          }}
          onClose={() => {
            removeFilter(filter);
          }}
        />
      );
    case 'dataClassification':
      return (
        <DataClassificationMultiSelect
          {...filter}
          value={(filterValues[filter.id] as string[]) || []}
          mode="multiple"
          setValue={(x: string[]) => setFilterValue(filter.id, x, filter)}
          label={filter.label}
          key={filter.id}
          onClose={() => {
            removeFilter(filter);
          }}
          displayButtonClassName={filter.wrapperClassName}
        />
      );
    case 'tenant':
      return (
        <TenantFC
          {...filter}
          key={filter.id}
          value={(filterValues[filter.id] as string[]) || []}
          setValue={(x) => setFilterValue(filter.id, x, filter)}
          filters={filters}
          filteredValues={filterValues}
          onClose={() => {
            removeFilter(filter);
          }}
        />
      );
    case 'tags': {
      const tagKey = filterValues[filter.tagKeyAttribute] as string;
      const tagValue = filterValues[filter.tagValueAttribute] as string;
      const operator = filterValues[
        filter.tagOperatorAttribute
      ] as ComparisonOperator;
      return (
        <TagFilter
          {...filter}
          key={filter.id}
          value={{
            key: tagKey,
            value: tagValue,
            operator: operator,
          }}
          setValue={(x) => {
            setFilterValue(filter.tagKeyAttribute, x.key, filter);
            setFilterValue(filter.tagValueAttribute, x.value, filter);
            setFilterValue(filter.tagOperatorAttribute, x.operator, filter);
          }}
        />
      );
    }
    case 'multiSelectWithSuggestionsInput': {
      const raw =
        (filterValues[filter.id] as (string | { tag: string })[]) || [];
      const value = raw.map((v) => (typeof v === 'string' ? v : v.tag));
      const handleChange = (x: (string | { label: string; value: string })[]) =>
        setFilterValue(
          filter.id,
          x.map((v) => (typeof v === 'string' ? v : v.value)),
          filter,
        );
      return (
        <MultiSelectWithSuggestions
          {...filter}
          value={value}
          onChange={handleChange}
          onInputTextChange={filter.onInputTextChange}
          suggestions={filter.suggestions || []}
          placeholder={filter.label}
          loading={filter.loading}
          disabled={filter.disabled}
          error={filter.error}
          className={filter.wrapperClassName}
        />
      );
    }
    default:
      return <></>;
  }
};
